import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { NAV, COMPANY } from "../lib/siteData";
import { Logo } from "./Logo";

export const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-[background,box-shadow,backdrop-filter] duration-300 ${
                scrolled
                    ? "backdrop-blur-xl bg-hhc-ivory/85 shadow-[0_1px_0_0_rgba(31,35,33,0.08)]"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                <Link to="/" data-testid="header-logo-link" aria-label="HEYHOMECAN home">
                    <Logo />
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === "/"}
                            data-testid={n.testId}
                            className={({ isActive }) =>
                                `nav-link text-[13px] tracking-[0.2em] uppercase font-medium ${
                                    isActive ? "active" : ""
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden md:flex">
                    <a
                        href={COMPANY.whatsapp}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="header-cta-whatsapp"
                        className="btn-primary text-[13px]"
                    >
                        <MessageCircle size={16} strokeWidth={1.75} />
                        Start a project
                    </a>
                </div>

                <button
                    type="button"
                    className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-hhc-line text-hhc-ink"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle menu"
                    data-testid="header-menu-toggle"
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </div>

            {/* Mobile sheet */}
            {open && (
                <div
                    className="md:hidden bg-hhc-ivory border-t border-hhc-line"
                    data-testid="mobile-menu"
                >
                    <div className="px-6 py-6 flex flex-col gap-4">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                data-testid={`mobile-${n.testId}`}
                                className={({ isActive }) =>
                                    `text-[15px] tracking-[0.14em] uppercase py-2 border-b border-hhc-line/70 ${
                                        isActive
                                            ? "text-hhc-clay"
                                            : "text-hhc-ink"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <a
                            href={COMPANY.whatsapp}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="mobile-cta-whatsapp"
                            className="btn-primary justify-center text-[13px] mt-2"
                        >
                            <MessageCircle size={16} />
                            Start a project
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
